import { Badge, Box, Button, Flex, Grid, TextInput, Text } from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { useDebouncedState, useHotkeys } from "@mantine/hooks";
import {
  IconCalendar,
  IconDiscount,
  IconSearch,
  IconTag,
} from "@tabler/icons-react";
import {
  MantineReactTable,
  MRT_ColumnDef,
  MRT_RowSelectionState,
  useMantineReactTable,
} from "mantine-react-table";
import React, { useDeferredValue, useEffect, useState } from "react";
import {
  keySearchProfitRevenueCTKM,
  repositoryMdm,
} from "../../../_base/_const/_constVar";
import _breadcrumb from "../../../_base/component/_layout/_breadcrumb";
import { changeSearchValue } from "../../../_base/helper/FunctionHelper";
import { _localization } from "../../../config/location";
import { MessageResponse } from "../../../model/MessageResponse";
import { formatDateTransfer } from "../../../common/FormatDate/FormatDate";

type ColumnPinningType = {
  left?: string[];
  right?: string[];
};

const ProfitRevenue = ({
  isModal,
  promotionCode,
}: {
  isModal: boolean;
  promotionCode?: string;
}) => {
  const headerRef = React.useRef<HTMLDivElement>(null);
  const [data, setData] = useState<any[]>([]);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [height, setHeight] = useState(0);
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 20,
  });
  const rowSelectionDeferred =
    useDeferredValue<MRT_RowSelectionState>(rowSelection);
  const [selectIds, setSelectIds] = useState<string[]>([]);

  const _searchJson = sessionStorage.getItem(keySearchProfitRevenueCTKM)
    ? JSON.parse(sessionStorage.getItem(keySearchProfitRevenueCTKM) ?? "")
    : null;
  const checkedPinSearch = localStorage.getItem("checkedPinSearch") == "true";
  const [search, setSearch] = useState<any>({
    FromDate:
      _searchJson && checkedPinSearch && !isModal ? _searchJson?.StartDate : "",
    ToDate:
      _searchJson && checkedPinSearch && !isModal ? _searchJson?.EndDate : "",
  });
  const [keySearch, setKeySearch] = useDebouncedState<string>(
    _searchJson && checkedPinSearch && !isModal ? _searchJson?.keySearch : "",
    300
  );
  const [MaChuongTrinh, setMaChuongTrinh] = useDebouncedState<string>(
    _searchJson && checkedPinSearch && !isModal
      ? _searchJson?.MaChuongTrinh
      : "",
    300
  );
  const [TenChuongTrinh, setTenChuongTrinh] = useDebouncedState<string>(
    _searchJson && checkedPinSearch && !isModal
      ? _searchJson?.TenChuongTrinh
      : "",
    300
  );

  const columns = React.useMemo<MRT_ColumnDef<any>[]>(
    () => [
      {
        accessorKey: "maChuongTrinh",
        header: "Mã chương trình",
        Cell: ({ renderedCellValue }) => (
          <Badge color={renderedCellValue ? "green" : "red"} variant="dot">
            {renderedCellValue ? renderedCellValue : "Không xác định"}
          </Badge>
        ),
      },
      { accessorKey: "tenChuongTrinh", header: "Tên chương trình" },
      {
        accessorKey: "tuNgay",
        header: "Từ ngày",
        Cell: ({ renderedCellValue }: any) => (
          <Text size="12.5px" fw={"500"}>
            {renderedCellValue && formatDateTransfer(renderedCellValue)}
          </Text>
        ),
      },
      {
        accessorKey: "denNgay",
        header: "Đến ngày",
        Cell: ({ renderedCellValue }: any) => (
          <Text size="12.5px" fw={"500"}>
            {renderedCellValue && formatDateTransfer(renderedCellValue)}
          </Text>
        ),
      },
      {
        accessorKey: "soLuongBanDuKien",
        header: "Số lượng bán dự kiến",
        size: 225,
      },
      {
        accessorKey: "dtDuTinh",
        header: "Doanh thu dự tính",
        Cell: ({ renderedCellValue }) =>
          renderedCellValue || renderedCellValue === 0 ? (
            <Text fw={500} size="12.5px">
              {Number(renderedCellValue)?.toLocaleString()} VNĐ
            </Text>
          ) : (
            <></>
          ),
      },
      {
        accessorKey: "lngDuKien",
        header: "LNG dự kiến",
        Cell: ({ renderedCellValue }) =>
          renderedCellValue || renderedCellValue === 0 ? (
            <Text fw={500} size="12.5px">
              {Number(renderedCellValue)?.toLocaleString()} VNĐ
            </Text>
          ) : (
            <></>
          ),
      },
      {
        accessorKey: "lngDtDuKien",
        header: "LNG doanh thu dự kiến",
        size: 225,
        Cell: ({ renderedCellValue }) =>
          renderedCellValue || renderedCellValue === 0 ? (
            <Text fw={500} size="12.5px">
              {renderedCellValue} %
            </Text>
          ) : (
            <></>
          ),
      },
      {
        accessorKey: "chiPhiDuTinh",
        header: "Chi phí dự tính",
        Cell: ({ renderedCellValue }) =>
          renderedCellValue || renderedCellValue === 0 ? (
            <Text fw={500} size="12.5px">
              {Number(renderedCellValue)?.toLocaleString()} VNĐ
            </Text>
          ) : (
            <></>
          ),
      },
      {
        accessorKey: "chiPhiDt",
        header: "Chi phí DT",
        Cell: ({ renderedCellValue }) =>
          renderedCellValue || renderedCellValue === 0 ? (
            <Text fw={500} size="12.5px">
              {renderedCellValue} %
            </Text>
          ) : (
            <></>
          ),
      },
      {
        accessorKey: "hangNccTaiTro",
        header: "Hãng NCC tài trợ",
        Cell: ({ renderedCellValue }) =>
          renderedCellValue || renderedCellValue === 0 ? (
            <Text fw={500} size="12.5px">
              {Number(renderedCellValue)?.toLocaleString()} VNĐ
            </Text>
          ) : (
            <></>
          ),
      },
      {
        accessorKey: "cfHacomTamTinh",
        header: "CF Hacom tạm tính",
        Cell: ({ renderedCellValue }) =>
          renderedCellValue || renderedCellValue === 0 ? (
            <Text fw={500} size="12.5px">
              {Number(renderedCellValue)?.toLocaleString()} VNĐ
            </Text>
          ) : (
            <></>
          ),
      },
    ],
    []
  );

  const fetchData = async () => {
    setIsLoading(true);
    setIsRefetching(true);
    setIsError(false);
    try {
      let url = `/api/v1/ChuongTrinhKhuyenMai/get-list?Skip=${
        pagination.pageIndex * pagination.pageSize
      }&Take=${pagination.pageSize}${
        promotionCode ? "&MaChuongTrinh=" + promotionCode : ""
      }`;

      const params = new URLSearchParams();
      Object.entries(search).forEach(([key, value]) => {
        if (Array.isArray(value) && value.length > 0) {
          value.forEach((item) => params.append(key, String(item)));
        } else if (typeof value === "string" && value.length > 0) {
          params.append(key, value);
        }
      });

      if (keySearch.length > 0) {
        url += `&KeySearch=${keySearch?.trim()}`;
      }

      if (MaChuongTrinh.length > 0 && !promotionCode) {
        url += `&MaChuongTrinh=${MaChuongTrinh?.trim()}`;
      }

      if (TenChuongTrinh.length > 0 && !promotionCode) {
        url += `&TenChuongTrinh=${TenChuongTrinh?.trim()}`;
      }

      url += `&${params.toString()}`;

      const res = await repositoryMdm.get<MessageResponse<any>>(url);

      if (res && res.success) {
        const result = res.data;
        setData(result);
        setRowCount(res.totalCount);
      } else {
        setData([]);
      }
    } catch (error) {
      setData([]);
      setIsError(true);
      console.error(error);
      return;
    } finally {
      setIsLoading(false);
      setIsRefetching(false);
      if (!isModal) {
        sessionStorage.setItem(
          keySearchProfitRevenueCTKM,
          JSON.stringify({
            ...search,
            keySearch: keySearch,
            MaChuongTrinh: MaChuongTrinh,
            TenChuongTrinh: TenChuongTrinh,
          })
        );
      }
    }
  };

  useEffect(() => {
    fetchData();
  }, [pagination?.pageIndex, pagination?.pageSize]);

  const getColumnPinningStorage = () => {
    const columnPinningStorage = localStorage.getItem(
      "columnPinningProfitRevenueCTKM"
    );
    return columnPinningStorage
      ? JSON.parse(columnPinningStorage)
      : {
          left: ["mrt-row-select", "mrt-row-numbers", "maChuongTrinh"],
          right: ["action"],
        }; // Giá trị mặc định
  };

  // Lưu columnPinning vào state
  const [columnPinning, setColumnPinning] = useState<ColumnPinningType>(
    getColumnPinningStorage()
  );
  // Hàm lưu vào localStorage
  useEffect(() => {
    localStorage.setItem(
      "columnPinningProfitRevenueCTKM",
      JSON.stringify(columnPinning)
    );
  }, [columnPinning]);

  useEffect(() => {
    //   startTransition(() => {
    const valuesList = Object.keys(rowSelectionDeferred);
    setSelectIds(valuesList);
    if (valuesList.length < 1) setSelectIds([]);
    else {
      const valuesList = Object.keys(rowSelectionDeferred);
      setSelectIds(valuesList);
    }
    //  });
  }, [rowSelectionDeferred]);

  useEffect(() => {
    const headerHeight = headerRef.current?.offsetHeight || 0;
    const handleResize = () => {
      setHeight(
        isModal
          ? window.innerHeight - (250 + headerHeight)
          : window.innerHeight - (185 + headerHeight)
      );
    };

    handleResize(); // Set initial height
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const table = useMantineReactTable({
    columns,
    data,
    enableColumnResizing: false,
    positionToolbarAlertBanner: "bottom",
    enableRowNumbers: true,
    //add custom action buttons to top-left of top toolbar
    enableTopToolbar: false,
    mantineTopToolbarProps: {
      style: {
        borderBottom: "3px solid rgba(128, 128, 128, 0.5)",
        marginBottom: 5,
      },
    },
    localization: _localization,
    getRowId: (row) => row.id?.toString(),
    initialState: {
      showColumnFilters: false,
      columnPinning: {
        // left: ["mrt-row-numbers", "maVt"],
        // right: ["action"],
        ...columnPinning,
      },
      columnVisibility: { id: true, phone: false, fax: false },
      density: "xs",
    },
    mantineTableContainerProps: {
      style: { maxHeight: height, minHeight: height },
    },
    enableStickyHeader: true,
    manualPagination: true,
    onColumnPinningChange: (newState) => {
      setColumnPinning(newState);
    },
    onRowSelectionChange: setRowSelection,
    rowCount: rowCount,
    onPaginationChange: setPagination,
    mantineTableBodyCellProps: ({ row }) => ({
      style: {
        fontWeight: "500", //conditional styling
        fontSize: "12.5px",
        padding: "5px 15px",
      },
    }),
    state: {
      isLoading,
      pagination: pagination,
      showAlertBanner: isError,
      showProgressBars: isRefetching,
      showSkeletons: isLoading, //loading for the first time with no data
      rowSelection,
      columnPinning,
    },
    mantineToolbarAlertBannerProps: isError
      ? { color: "red", children: "Lỗi tải dữ liệu !" }
      : undefined,
    mantinePaginationProps: {
      showRowsPerPage: true,
      withEdges: true,
      rowsPerPageOptions: ["20", "50", "100"],
    },
    paginationDisplayMode: "pages",
    enableColumnPinning: true,
    mantineTableProps: {
      striped: false,
    },
    columnFilterDisplayMode: "popover",
    mantineTableBodyRowProps: ({ row }) => ({
      style: { cursor: "pointer" },
    }),
  });

  useHotkeys([
    [
      "Enter",
      () => {
        fetchData();
      },
    ],
  ]);

  return (
    <>
      <div
        ref={headerRef}
        style={{
          width: "100%",
        }}
      >
        {!isModal ? (
          <Flex justify={"space-between"} align={"center"} w={"100%"}>
            <_breadcrumb></_breadcrumb>
          </Flex>
        ) : (
          <></>
        )}
        <Grid mt={10} w={"100%"}>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <TextInput
              placeholder="Nhập từ khóa tìm kiếm"
              defaultValue={keySearch}
              onChange={(e) => setKeySearch(e.currentTarget.value)}
              leftSection={<IconSearch color="#15aabf" />}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  fetchData();
                }
              }}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <DatePickerInput
              size="sm"
              placeholder="Chọn ngày bắt đầu"
              locale="vi"
              valueFormat="DD/MM/YYYY"
              clearable
              value={
                search.FromDate && search.FromDate?.length > 0
                  ? new Date(search.FromDate ?? "")
                  : null
              }
              maxDate={
                search.ToDate && search.ToDate?.length > 0
                  ? new Date(search.ToDate)
                  : new Date()
              }
              onChange={(e) => {
                changeSearchValue(
                  e
                    ? new Date(
                        new Date(e).getTime() + 7 * 3600 * 1000
                      )?.toISOString()
                    : "",
                  "FromDate",
                  setSearch
                );
              }}
              leftSection={<IconCalendar color="#15aabf" />}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  fetchData();
                }
              }}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <DatePickerInput
              size="sm"
              placeholder="Chọn ngày kết thúc"
              locale="vi"
              valueFormat="DD/MM/YYYY"
              clearable
              value={
                search.ToDate && search.ToDate?.length > 0
                  ? new Date(search.ToDate ?? "")
                  : null
              }
              minDate={
                search.FromDate && search.FromDate?.length > 0
                  ? new Date(search.FromDate)
                  : undefined
              }
              onChange={(e) => {
                changeSearchValue(
                  e
                    ? new Date(
                        new Date(e).getTime() + 7 * 3600 * 1000
                      )?.toISOString()
                    : "",
                  "ToDate",
                  setSearch
                );
              }}
              leftSection={<IconCalendar color="#15aabf" />}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  fetchData();
                }
              }}
            />
          </Grid.Col>
          {!isModal ? (
            <>
              <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
                <TextInput
                  placeholder="Nhập mã CTKM"
                  defaultValue={MaChuongTrinh}
                  onChange={(e) => setMaChuongTrinh(e.currentTarget.value)}
                  leftSection={<IconDiscount color="#15aabf" />}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      fetchData();
                    }
                  }}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
                <TextInput
                  placeholder="Nhập tên CTKM"
                  defaultValue={TenChuongTrinh}
                  onChange={(e) => setTenChuongTrinh(e.currentTarget.value)}
                  leftSection={<IconTag color="#15aabf" />}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      fetchData();
                    }
                  }}
                />
              </Grid.Col>
            </>
          ) : (
            <></>
          )}
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <Button
              onClick={() => fetchData()}
              leftSection={<IconSearch size={14} />}
            >
              Tìm kiếm
            </Button>
          </Grid.Col>
        </Grid>
      </div>
      <Box mt={10}>
        <MantineReactTable table={table} />
      </Box>
    </>
  );
};

export default ProfitRevenue;
